import { useState, useEffect, useId } from "react";
import { motion } from "framer-motion";
import { cn } from "../../lib/cn";
import { LeverMark } from "../../lib/brand";
import type { AgentStatus } from "../../types/ai";

const PHASE_LABELS: Record<AgentStatus, string> = {
  idle: "",
  thinking: "Thinking",
  reading: "Reading document",
  compiling: "Compiling",
  editing: "Editing",
  done: "Done",
};

export function ThinkingIndicator({
  status,
  documentFilename,
}: {
  status: AgentStatus;
  documentFilename?: string;
}) {
  const gradientId = `thinking-grad-${useId()}`;
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    setElapsed(0);
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [status]);

  const label = (() => {
    if (status === "reading" && documentFilename) {
      return `Reading ${documentFilename}`;
    }
    if (status === "editing" && documentFilename) {
      return `Editing ${documentFilename}`;
    }
    return PHASE_LABELS[status] || "Thinking";
  })();

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.15 }}
      className="flex gap-2.5 px-3 py-2"
    >
      {/* Animated logo with ring */}
      <div className="relative flex h-5 w-5 shrink-0 items-center justify-center mt-0.5">
        <svg
          className="thinking-ring absolute inset-0"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
        >
          <circle
            cx="10"
            cy="10"
            r="9"
            stroke={`url(#${gradientId})`}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeDasharray="14 42"
          />
          <defs>
            <linearGradient
              id={gradientId}
              x1="0"
              y1="0"
              x2="20"
              y2="20"
            >
              <stop stopColor="var(--color-lever-orange)" />
              <stop offset="1" stopColor="var(--color-lever-orange)" stopOpacity="0" />
            </linearGradient>
          </defs>
        </svg>
        <LeverMark size={10} className="thinking-logo" />
      </div>

      {/* Status text + timer */}
      <div className="flex items-center gap-2 pt-0.5 min-w-0">
        <span
          className={cn(
            "text-[11px] font-medium",
            status === "done" ? "text-[var(--color-signal-green)]" : "thinking-shimmer",
          )}
        >
          {label}
          {status !== "done" && (
            <span className="thinking-dots" />
          )}
        </span>
        {elapsed > 1 && status !== "done" && (
          <span className="text-[9px] text-[var(--text-muted)] tabular-nums opacity-60">
            {elapsed}s
          </span>
        )}
      </div>
    </motion.div>
  );
}
